import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  CalendarIcon,
  CogIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  ClipboardListIcon,
  ClockIcon,
  ChartBarIcon,
  TrendingUpIcon,
} from '@heroicons/react/outline';
import DateRangePicker from '../components/DateRangePicker';
import SimpleBarChart from '../components/charts/SimpleBarChart';

const defaultSettings = {
  technicianHoursPerForm: 0.5,
  managementHoursPerReport: 0.5,
  zarPerHourTechnician: 250,
  zarPerHourManagement: 250,
  notes: '',
};

const numericSettingKeys = [
  'technicianHoursPerForm',
  'managementHoursPerReport',
  'zarPerHourTechnician',
  'zarPerHourManagement',
];

const toDateTimeStr = d => {
  const tzOffset = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - tzOffset)
    .toISOString()
    .slice(0, 19)
    .replace('T', ' ');
};

const toPickerValue = d => {
  const tzOffset = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - tzOffset).toISOString().slice(0, 19);
};

const parsePickerValue = value => {
  if (!value) return null;
  const normalized = value.length === 16 ? `${value}:00` : value;
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatRange = (startDate, endDate) => {
  const fmt = d =>
    d.toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  return `${fmt(startDate)} - ${fmt(endDate)}`;
};

const formatCurrency = value =>
  new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value || 0);

const formatHours = value => {
  const safe = Number.isFinite(value) ? value : 0;
  return `${new Intl.NumberFormat('en-ZA', {
    minimumFractionDigits: safe % 1 === 0 ? 0 : 1,
    maximumFractionDigits: 2,
  }).format(safe)} hrs`;
};

function DrivePage() {
  const [startDate, setStartDate] = useState(() => {
    const end = new Date();
    return new Date(end.getTime() - 24 * 60 * 60 * 1000);
  });
  const [endDate, setEndDate] = useState(() => new Date());
  const [showPicker, setShowPicker] = useState(false);

  const [settings, setSettings] = useState(defaultSettings);
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [settingsForm, setSettingsForm] = useState(defaultSettings);
  const [settingsErrors, setSettingsErrors] = useState({});

  const [kpis, setKpis] = useState({ totalForms: 0, totalReports: 0, productTonnage: 0 });
  const [trend, setTrend] = useState([]);

  const startStr = useMemo(() => toDateTimeStr(startDate), [startDate]);
  const endStr = useMemo(() => toDateTimeStr(endDate), [endDate]);

  useEffect(() => {
    fetch('/api/drive-api.php?action=settings')
      .then(res => res.json())
      .then(res => {
        const loaded = { ...defaultSettings, ...(res.settings || {}) };
        setSettings(loaded);
        setSettingsForm(loaded);
      })
      .catch(() => {
        setSettings(defaultSettings);
        setSettingsForm(defaultSettings);
      });
  }, []);

  useEffect(() => {
    fetch(`/api/drive-api.php?action=kpis&start=${encodeURIComponent(startStr)}&end=${encodeURIComponent(endStr)}`)
      .then(res => res.json())
      .then(res => {
        setKpis({
          totalForms: Number(res.totalForms) || 0,
          totalReports: Number(res.totalReports) || 0,
          productTonnage: Number(res.productTonnage) || 0,
        });
      })
      .catch(() => setKpis({ totalForms: 0, totalReports: 0, productTonnage: 0 }));
  }, [startStr, endStr]);

  useEffect(() => {
    fetch('/api/drive-api.php?action=trend')
      .then(res => res.json())
      .then(res => setTrend(Array.isArray(res.trend) ? res.trend : []))
      .catch(() => setTrend([]));
  }, [settings]);

  const totals = useMemo(() => {
    const totalForms = kpis.totalForms || 0;
    const totalReports = kpis.totalReports || 0;
    const productTonnage = kpis.productTonnage || 0;
    const technicianHoursSaved = totalForms * settings.technicianHoursPerForm;
    const managementVisibilityGains = totalReports * settings.managementHoursPerReport;
    const formValueAdd = technicianHoursSaved * settings.zarPerHourTechnician;
    const reportValueAdd = managementVisibilityGains * settings.zarPerHourManagement;
    const totalValueAdd = formValueAdd + reportValueAdd;

    return {
      totalForms,
      totalReports,
      productTonnage,
      technicianHoursSaved,
      managementVisibilityGains,
      formValueAdd,
      reportValueAdd,
      totalValueAdd,
    };
  }, [kpis, settings]);

  const trendBars = useMemo(
    () =>
      trend.map(point => ({
        label: point.bucket,
        value: Number(point.totalValueAdd) || 0,
        display: '',
        color: 'rgba(1, 89, 178, 0.85)',
        tooltip: `${point.bucket} • ${formatCurrency(Number(point.totalValueAdd) || 0)}`,
      })),
    [trend]
  );

  const trendMax = useMemo(() => Math.max(...trendBars.map(d => d.value), 0), [trendBars]);

  const openSetup = () => {
    setSettingsForm(settings);
    setSettingsErrors({});
    setShowSetupModal(true);
  };

  const updateField = (key, value) => {
    setSettingsForm(prev => ({ ...prev, [key]: value }));
    setSettingsErrors(prev => ({ ...prev, [key]: '' }));
  };

  const saveSettings = () => {
    const nextErrors = {};
    const normalized = {};

    numericSettingKeys.forEach(key => {
      const parsed = Number(settingsForm[key]);
      if (!Number.isFinite(parsed) || parsed < 0) {
        nextErrors[key] = 'Value must be a number greater than or equal to 0.';
      } else {
        normalized[key] = parsed;
      }
    });

    if (Object.keys(nextErrors).length) {
      setSettingsErrors(nextErrors);
      return;
    }

    fetch('/api/drive-api.php?action=settings', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...normalized, notes: settingsForm.notes || '' }),
    })
      .then(res => res.json())
      .then(res => {
        const saved = { ...defaultSettings, ...(res.settings || normalized) };
        setSettings(saved);
        setSettingsForm(saved);
        setShowSetupModal(false);
      })
      .catch(() => {
        setSettings(normalized);
        setShowSetupModal(false);
      });
  };

  const cards = [
    {
      key: 'totalValueAdd',
      title: 'Total Value Add',
      value: formatCurrency(totals.totalValueAdd),
      description: 'Combined value from form and report gains.',
      icon: CurrencyDollarIcon,
    },
    {
      key: 'valueAddPerTonne',
      title: 'Value add per Product Ton',
      value: formatCurrency(totals.productTonnage > 0 ? totals.totalValueAdd / totals.productTonnage : 0),
      description: `Total Value Add ÷ Product Tonnage (${new Intl.NumberFormat('en-ZA', { maximumFractionDigits: 2 }).format(totals.productTonnage)} t)`,
      icon: TrendingUpIcon,
    },
    {
      key: 'totalForms',
      title: 'Total Forms',
      value: `${totals.totalForms}`,
      description: 'Total Forms within selected range.',
      icon: DocumentTextIcon,
    },
    {
      key: 'totalReports',
      title: 'Total Reports',
      value: `${totals.totalReports}`,
      description: 'Total Reports within selected range.',
      icon: ClipboardListIcon,
    },
    {
      key: 'technicianHoursSaved',
      title: 'Technician Hours Saved',
      value: formatHours(totals.technicianHoursSaved),
      description: `Total Forms × ${settings.technicianHoursPerForm} hrs/form`,
      icon: ClockIcon,
    },
    {
      key: 'managementVisibilityGains',
      title: 'Management Visibility Gains',
      value: formatHours(totals.managementVisibilityGains),
      description: `Total Reports × ${settings.managementHoursPerReport} hrs/report`,
      icon: ChartBarIcon,
    },
    {
      key: 'formValueAdd',
      title: 'Form Value Add',
      value: formatCurrency(totals.formValueAdd),
      description: `Technician Hours Saved × ${formatCurrency(settings.zarPerHourTechnician)}/hr`,
      icon: CurrencyDollarIcon,
    },
    {
      key: 'reportValueAdd',
      title: 'Report Value Add',
      value: formatCurrency(totals.reportValueAdd),
      description: `Management Visibility Gains × ${formatCurrency(settings.zarPerHourManagement)}/hr`,
      icon: TrendingUpIcon,
    },
  ];

  return (
    <div className="space-y-5 text-slate-900">
      <div className="rounded-2xl bg-[#12344a]/90 p-5 ring-1 ring-white/10">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white">DRiVE</h1>
            <p className="text-sm text-slate-300">DRi Value Engine for cost savings and visibility gains.</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => setShowPicker(true)}
              className="inline-flex items-center gap-2 rounded-xl border border-white/15 bg-[#081620] px-3 py-2 text-sm text-white hover:border-[#22c55e]/60"
            >
              <CalendarIcon className="h-4 w-4 text-[#22c55e]" />
              <span>{formatRange(startDate, endDate)}</span>
            </button>
            <button
              type="button"
              onClick={openSetup}
              className="inline-flex items-center gap-2 rounded-xl bg-[#22c55e] px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-[#16a34a]"
            >
              <CogIcon className="h-4 w-4" />
              Value Setup
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {cards.map(card => {
          const Icon = card.icon;
          return (
            <div
              key={card.key}
              className={`rounded-2xl bg-white ${card.wide ? 'md:col-span-2' : ''}`}
            >
              <div className="flex items-center gap-2 rounded-t-2xl bg-[#0159B2] px-4 py-3 text-white">
                <Icon className="h-4 w-4" />
                <p className="text-sm font-semibold">{card.title}</p>
              </div>
              <div className="p-4">
                <p className="text-3xl font-bold text-slate-900">{card.value}</p>
                <p className="mt-2 text-sm text-slate-600">{card.description}</p>
              </div>
            </div>
          );
        })}
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <h2 className="mb-4 text-2xl font-semibold text-slate-900">Total Value Add Trend</h2>
        <div className="h-72">
          <SimpleBarChart
            data={trendBars}
            max={trendMax > 0 ? trendMax : 1}
            height={280}
            yLabel="ZAR"
            showValueLabels={false}
          />
        </div>
      </div>

      {showPicker && (
        <DateRangePicker
          initialStart={toPickerValue(startDate)}
          initialEnd={toPickerValue(endDate)}
          onApply={(start, end) => {
            const nextStart = parsePickerValue(start);
            const nextEnd = parsePickerValue(end);
            if (nextStart && nextEnd) {
              setStartDate(nextStart);
              setEndDate(nextEnd);
            }
          }}
          onClose={() => setShowPicker(false)}
        />
      )}

      <Transition appear show={showSetupModal} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setShowSetupModal(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-slate-900/80" aria-hidden="true" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-left">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-xl rounded-2xl border border-white/10 bg-[#081620] p-6 text-white shadow-2xl">
                  <Dialog.Title className="text-xl font-semibold">Value Setup</Dialog.Title>
                  <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {[
                      ['technicianHoursPerForm', 'Technician Hours per Form (hours)'],
                      ['managementHoursPerReport', 'Management Hours per Report (hours)'],
                      ['zarPerHourTechnician', 'Technician Rate (ZAR/hour)'],
                      ['zarPerHourManagement', 'Management Rate (ZAR/hour)'],
                    ].map(([key, label]) => (
                      <div key={key}>
                        <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-300">
                          {label}
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={settingsForm[key]}
                          onChange={e => updateField(key, e.target.value)}
                          className="w-full rounded-lg border border-white/15 bg-[#0b1d2b] px-3 py-2 text-sm text-white focus:border-[#22c55e] focus:outline-none"
                        />
                        {settingsErrors[key] ? (
                          <p className="mt-1 text-xs text-red-300">{settingsErrors[key]}</p>
                        ) : null}
                      </div>
                    ))}
                    <div className="sm:col-span-2">
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-300">
                        Notes
                      </label>
                      <textarea
                        value={settingsForm.notes || ''}
                        onChange={e => updateField('notes', e.target.value)}
                        rows={5}
                        placeholder="Add notes for value setup..."
                        className="w-full resize-y rounded-lg border border-white/15 bg-[#0b1d2b] px-3 py-2 text-sm text-white focus:border-[#22c55e] focus:outline-none"
                      />
                    </div>
                  </div>
                  <div className="mt-6 flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => setShowSetupModal(false)}
                      className="rounded-lg border border-white/20 px-4 py-2 text-sm hover:bg-white/5"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={saveSettings}
                      className="rounded-lg bg-[#22c55e] px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-[#16a34a]"
                    >
                      Save
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
}

export default DrivePage;
